// ─── Global search ───────────────────────────────────────────────
// The contract between the search workspace (apps/web) and the search service
// (apps/api). Area KEYS and their labels live here because both ends name them;
// the table wiring behind each key is API-only (services/search/areas.ts).
//
// Search reads the local synced mirror only — never a third-party API — and is
// open like every other read in PCP: it surfaces the same rows the per-area list
// pages already show. (f-01826)
/** Every area global search can look in. Order is the order they render. */
export const SEARCH_AREA_KEYS = [
    'backlog', 'topics', 'tickets', 'knowledge', 'documents', 'requests',
    'releases', 'commits', 'companies', 'employees', 'entitlements',
    'smartguides', 'incidents', 'coco', 'errors',
];
export const SEARCH_AREAS = [
    { key: 'backlog', label: 'Backlog items', short: 'Backlog', icon: 'backlog' },
    { key: 'topics', label: 'Topics', short: 'Topics', icon: 'topics' },
    { key: 'tickets', label: 'Tickets', short: 'Tickets', icon: 'tickets' },
    { key: 'knowledge', label: 'Knowledge Base', short: 'Knowledge', icon: 'documentation' },
    { key: 'documents', label: 'Documents', short: 'Documents', icon: 'folder' },
    { key: 'requests', label: 'Requests', short: 'Requests', icon: 'note' },
    { key: 'releases', label: 'Release notes', short: 'Releases', icon: 'releases' },
    { key: 'commits', label: 'Commits', short: 'Commits', icon: 'commits' },
    { key: 'companies', label: 'Companies', short: 'Companies', icon: 'companies' },
    { key: 'employees', label: 'Employees', short: 'Employees', icon: 'user' },
    { key: 'entitlements', label: 'Entitlements', short: 'Entitlements', icon: 'entitlements' },
    { key: 'smartguides', label: 'Smartguides', short: 'Smartguides', icon: 'guides' },
    { key: 'incidents', label: 'Status incidents', short: 'Incidents', icon: 'statusPage' },
    { key: 'coco', label: 'CoCo Config', short: 'CoCo', icon: 'settings' },
    { key: 'errors', label: 'App errors', short: 'Errors', icon: 'warning' },
];
/** Out of the box a search looks in the three primary content domains only —
 *  wide enough to be useful, narrow enough to stay readable. Every other area is
 *  one click away, and the set is per-user (Profile → Search). */
export const DEFAULT_SEARCH_AREAS = ['backlog', 'topics', 'tickets'];
const AREA_KEY_SET = new Set(SEARCH_AREA_KEYS);
/** Keep only real area keys, in canonical order, deduped. Anything unknown (an
 *  old bookmark, a hand-edited URL) is dropped rather than erroring. */
export function normalizeSearchAreas(input) {
    const wanted = new Set((input ?? []).filter((k) => AREA_KEY_SET.has(k)));
    return SEARCH_AREA_KEYS.filter((k) => wanted.has(k));
}
export function searchAreaMeta(key) {
    return SEARCH_AREAS.find((a) => a.key === key);
}
export const SEARCH_SORTS = ['relevance', 'updated', 'created'];
export const SEARCH_SORT_LABELS = {
    relevance: 'Relevance', updated: 'Recently updated', created: 'Recently created',
};
export const SEARCH_WINDOWS = ['any', '24h', '7d', '30d', '90d', 'year'];
export const SEARCH_WINDOW_LABELS = {
    any: 'Any time', '24h': 'Last 24 hours', '7d': 'Last 7 days',
    '30d': 'Last 30 days', '90d': 'Last 90 days', year: 'This year',
};
export function defaultSearchFilters(areas) {
    return {
        areas: [...(areas ?? DEFAULT_SEARCH_AREAS)],
        states: [],
        types: [],
        assignees: [],
        authors: [],
        tags: [],
        updatedWithin: 'any',
        createdWithin: 'any',
        reviewed: 'any',
        sort: 'relevance',
    };
}
/** How many filter controls are narrowing the result set right now — the count
 *  badge on the Filters button. Areas only counts when it differs from the
 *  user's own default set, so an untouched search reads as unfiltered. */
export function activeFilterCount(f, defaults) {
    const areasDiffer = f.areas.length !== defaults.length || f.areas.some((a) => !defaults.includes(a));
    return (areasDiffer ? 1 : 0)
        + (f.states.length ? 1 : 0)
        + (f.types.length ? 1 : 0)
        + (f.assignees.length ? 1 : 0)
        + (f.authors.length ? 1 : 0)
        + (f.tags.length ? 1 : 0)
        + (f.updatedWithin !== 'any' ? 1 : 0)
        + (f.createdWithin !== 'any' ? 1 : 0)
        + (f.reviewed !== 'any' ? 1 : 0);
}
export const CONFIDENCE_BAND_LABEL = {
    strong: 'Well grounded',
    partial: 'Partly grounded',
    thin: 'Thinly grounded',
};
export function confidenceBand(value) {
    if (value >= 75)
        return 'strong';
    if (value >= 50)
        return 'partial';
    return 'thin';
}
/**
 * How authoritative a KIND of record is, before anything about this particular
 * one is considered. A vetted, published article is evidence; an unresolved
 * error report is a symptom someone has not looked at yet.
 */
const AREA_AUTHORITY = {
    knowledge: 92, releases: 88, incidents: 84, commits: 80, coco: 80,
    requests: 78, backlog: 76, documents: 74, entitlements: 72,
    tickets: 70, companies: 70, employees: 70, smartguides: 70,
    topics: 62, errors: 45,
};
/** A state that means the record reached a conclusion — Danish included, since
 *  the ticket mirror stores "Lukket". */
const SETTLED = /closed|lukket|done|resolved|solved|completed|implemented|published|archived/i;
/** A state that means the record is still someone's open question. */
const UNSETTLED = /open|åben|new|ny|active|ready|pending|planning|investigating/i;
/** Age penalty, in points. Recency matters here more than in most retrieval:
 *  a control plane's records go stale as the system they describe changes. */
function agePenalty(updatedAt, now) {
    if (!updatedAt)
        return 12; // no date at all is its own small doubt
    const days = (now - new Date(updatedAt).getTime()) / 86_400_000;
    if (!Number.isFinite(days) || days < 0)
        return 0;
    if (days < 30)
        return 0;
    if (days < 90)
        return 5;
    if (days < 180)
        return 10;
    if (days < 365)
        return 16;
    if (days < 730)
        return 22;
    return 28;
}
/**
 * How far a single record can be trusted as evidence. Deliberately independent
 * of the query — that is what makes it a different reading from relevance.
 */
export function sourceConfidence(hit, now = Date.now()) {
    let v = AREA_AUTHORITY[hit.area] ?? 65;
    const state = hit.state ?? '';
    if (SETTLED.test(state))
        v += 8;
    else if (UNSETTLED.test(state))
        v -= 4;
    v -= agePenalty(hit.updated?.at ?? null, now);
    return Math.max(0, Math.min(100, Math.round(v)));
}
/** What each factor is worth in the composite. Grounding and retrieval dominate:
 *  an answer that cites nothing, or cites records that miss half the question,
 *  is not rescued by being recent. */
const FACTOR_WEIGHT = {
    grounding: 0.35, retrieval: 0.30, corroboration: 0.20, freshness: 0.15,
};
/** Weighted mean of the factors — the headline number, 0-100. */
export function answerConfidence(factors) {
    if (!factors.length)
        return 0;
    let sum = 0;
    let weight = 0;
    for (const f of factors) {
        const w = FACTOR_WEIGHT[f.key] ?? 0;
        sum += f.value * w;
        weight += w;
    }
    return weight ? Math.max(0, Math.min(100, Math.round(sum / weight))) : 0;
}
// ─── Question detection ──────────────────────────────────────────
const QUESTION_OPENERS = [
    'why', 'what', 'when', 'where', 'who', 'whom', 'whose', 'which', 'how',
    'is', 'are', 'was', 'were', 'do', 'does', 'did', 'can', 'could', 'should',
    'would', 'will', 'has', 'have', 'had', 'am',
    // Danish — the forum and half the tickets are written in it.
    'hvorfor', 'hvad', 'hvornår', 'hvor', 'hvem', 'hvilken', 'hvilke', 'hvordan',
    'er', 'var', 'kan', 'skal', 'vil', 'har', 'blev',
];
/**
 * Does this query read as a question worth spending an answer on?
 *
 * AI mode returns the ranked list either way; the ANSWER card is what this
 * gates, so flipping the toggle never makes a plain lookup slower. Shared so the
 * server's decision and the UI's expectation can't drift.
 *
 * A question mark settles it. Otherwise we need an interrogative opener AND
 * enough words to be a sentence rather than a keyword — "how" alone is someone
 * searching for the word "how".
 */
export function looksLikeQuestion(query) {
    const q = String(query || '').trim();
    if (!q)
        return false;
    if (q.includes('?'))
        return true;
    const words = q.toLowerCase().split(/\s+/).filter(Boolean);
    if (words.length < 4)
        return false;
    return QUESTION_OPENERS.includes(words[0].replace(/[^a-zæøå]/g, ''));
}
export function defaultSearchPreferences() {
    return { areas: [...DEFAULT_SEARCH_AREAS], mode: 'classic' };
}
//# sourceMappingURL=search.js.map